import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { QStack } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import type { Message } from '@captivateiq/assistant/types';
import { ThinkingComplete } from './ThinkingComplete';
import { ThinkingLoader } from './ThinkingLoader';
import { AgentMessage } from './AgentMessage';
import { ErrorMessage } from './ErrorMessage';
import { MessageButtonBar } from './MessageButtonBar';
import { UserMessage } from './UserMessage';

import { TabLayout } from '../../TabLayout';
import { AgentIntroduction } from '../shared/AgentIntroduction';
import { MessageInput } from '../shared/MessageInput';

export const ChatView = () => {
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const thinkingStartTimeRef = useRef<number | null>(null);
  const [lastThinkingDuration, setLastThinkingDuration] = useState<number | null>(null);

  const {
    selectedAgent,
    currentConversation,
    send,
    agentThinking,
    messageSendError,
    getMessageButtons,
    getMessageAppActions,
    getPanelButtons,
  } = useAIShellContext();

  // Track thinking duration
  useEffect(() => {
    if (agentThinking) {
      thinkingStartTimeRef.current = Date.now();
      setLastThinkingDuration(null);
    } else if (thinkingStartTimeRef.current !== null) {
      const elapsed = Math.round((Date.now() - thinkingStartTimeRef.current) / 1000);
      setLastThinkingDuration(elapsed);
      thinkingStartTimeRef.current = null;
    }
  }, [agentThinking]);

  const messages = useMemo(() => currentConversation?.messages || [], [currentConversation?.messages]);

  const sendAndScroll = useCallback(
    async (message: string) => {
      if (agentThinking) {
        return;
      }
      bottomSentinelRef.current?.scrollIntoView({ behavior: 'auto' });
      await send(message);
    },
    [send, agentThinking],
  );

  useEffect(() => {
    bottomSentinelRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages]);

  return (
    <TabLayout bottomContent={<MessageInput agentThinking={agentThinking} onSubmit={sendAndScroll} />}>
      <AgentIntroduction agentName={selectedAgent ?? ''} />
      <QStack
        direction="column"
        spacing="10px"
        sx={{
          alignItems: 'stretch',
          backgroundColor: 'grey.50',
          flex: 1,
          padding: '16px',
        }}
      >
        <MessageButtonBar align="right" buttons={getPanelButtons()} />
        {messages.map((message: Message, index: number) => {
          const buttons = getMessageButtons(message.id);
          const isLastAgentMessage =
            message.type === 'agent' && !messages.slice(index + 1).some((m) => m.type === 'agent');

          switch (message.type) {
            case 'user':
              return <UserMessage buttons={buttons} key={message.id} message={message} />;
            case 'agent':
              return (
                <React.Fragment key={message.id}>
                  {isLastAgentMessage && !agentThinking && lastThinkingDuration !== null && (
                    <ThinkingComplete elapsedSeconds={lastThinkingDuration} />
                  )}
                  <AgentMessage appActions={getMessageAppActions(message.id)} buttons={buttons} message={message} />
                </React.Fragment>
              );
            default:
              return null;
          }
        })}
        {agentThinking && <ThinkingLoader />}
        {messageSendError && <ErrorMessage message={messageSendError} />}
        <div ref={bottomSentinelRef} />
      </QStack>
    </TabLayout>
  );
};
